// In-app help content (docs/ at the repo root, bundled at BUILD time via
// Vite's `?raw` import suffix — see apps/web/src/vite-env.d.ts for the module
// declaration; no vite.config.ts change was needed, the pnpm workspace root
// is already inside the dev server's default fs allowlist). Editing a guide
// has zero effect on a deployed environment until the web app is rebuilt and
// redeployed (docs/ADMIN_GUIDE.md §7 has the same note).
import walkthroughRaw from '../../../../../docs/WALKTHROUGH.md?raw';
import userGuideRaw from '../../../../../docs/USER_GUIDE.md?raw';
import playbookRaw from '../../../../../docs/OPERATIONS_PLAYBOOK.md?raw';
import adminGuideRaw from '../../../../../docs/ADMIN_GUIDE.md?raw';
import { parseDoc, type ParsedDoc } from './sections';

export type HelpDocId = 'walkthrough' | 'user' | 'playbook' | 'admin';

export interface HelpDoc {
  id: HelpDocId;
  title: string;
  content: string;
}

/** The four finished guides (docs/WALKTHROUGH.md, docs/USER_GUIDE.md,
 * docs/OPERATIONS_PLAYBOOK.md, docs/ADMIN_GUIDE.md), keyed the same way
 * helpMap.ts's `HelpSectionRef.doc` refers to them. */
export const HELP_DOCS: Record<HelpDocId, HelpDoc> = {
  walkthrough: { id: 'walkthrough', title: 'Getting started — a guided walkthrough', content: walkthroughRaw },
  user: { id: 'user', title: 'User Guide', content: userGuideRaw },
  playbook: { id: 'playbook', title: 'Operations Playbook', content: playbookRaw },
  admin: { id: 'admin', title: 'Administrator Guide', content: adminGuideRaw },
};

/** Each guide parsed exactly ONCE, at module scope (the guides are static
 * build-time strings, so this is a pure, one-off computation at bundle-eval
 * time). Everything downstream — the /help TOC, HelpDrawer's section
 * extraction, and markdown.tsx's rendered heading ids — reads these
 * precomputed headings instead of re-parsing or re-slugging at render time.
 * This is the single slug-computation path: rendered ids can never diverge
 * from the slugs helpMap.test.ts verifies. */
export const PARSED_DOCS: Record<HelpDocId, ParsedDoc> = {
  walkthrough: parseDoc(walkthroughRaw),
  user: parseDoc(userGuideRaw),
  playbook: parseDoc(playbookRaw),
  admin: parseDoc(adminGuideRaw),
};

/** Display order for the /help centre (HelpCentre.tsx): the new-user
 * walkthrough leads (it's the front door for someone who's never opened the
 * app), then the user guide (day-to-day reference), then the playbook
 * (discipline/cadence), then the admin guide (one-off setup) — mirrors the
 * reading order USER_GUIDE.md §1 recommends, with the walkthrough now ahead
 * of it as the very first thing a brand-new user should read. */
export const HELP_DOC_ORDER: HelpDocId[] = ['walkthrough', 'user', 'playbook', 'admin'];
